import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatBs, formatUsd, formatCop } from "@/lib/format";
import { computeCashClose } from "@/components/pos/cashCloseMath";

const CHIP = {
    BS: "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300",
    USD: "border-emerald-600/40 bg-emerald-600/10 text-emerald-700 dark:text-emerald-300",
    COP: "border-[#8f1433] bg-[#8f1433] text-white",
};
const STATUS = {
    cuadrado: { label: "Cuadrado", cls: "border-emerald-600/40 bg-emerald-600/15 text-emerald-700 dark:text-emerald-300" },
    sobrante: { label: "Sobrante", cls: "border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300" },
    faltante: { label: "Faltante", cls: "border-rose-600/40 bg-rose-600/15 text-rose-700 dark:text-rose-300" },
    pendiente: { label: "Pendiente de conteo", cls: "border-border bg-secondary/60 text-muted-foreground" },
};
const COUNT_KEYS = ["closing_bs", "closing_usd", "closing_cop"];

const Chip = ({ cur }) => (
    <span className={`inline-flex min-w-[42px] justify-center rounded-full border px-2 py-0.5 text-[11px] font-bold ${CHIP[cur]}`}>{cur === "BS" ? "Bs" : cur}</span>
);

const Row = ({ label, value, strong, testid }) => (
    <div className={`flex items-center justify-between gap-3 py-1.5 ${strong ? "border-t border-border pt-2 font-bold" : ""}`}>
        <span className={strong ? "" : "text-muted-foreground"}>{label}</span>
        <span className="font-mono tabular-nums" data-testid={testid}>{value}</span>
    </div>
);

const signedBs = (n) => `${n > 0 ? "+" : ""}${formatBs(n)} Bs`;

export const CloseSessionDialog = ({ open, onOpenChange, session, rate, form, setForm, onSubmit, pending }) => {
    const rates = { bs_per_usd: rate?.bs_per_usd, bs_per_cop: rate?.bs_per_cop };
    const c = computeCashClose(session || {}, form, rates);
    const counted = COUNT_KEYS.some((k) => (parseFloat(form[k]) || 0) !== 0);
    const status = counted ? c.status : "pendiente";
    const st = STATUS[status];
    const field = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-card border-border max-w-lg max-h-[92vh] overflow-y-auto" data-testid="pos-close-session-dialog">
                <DialogHeader><DialogTitle>Cerrar caja</DialogTitle></DialogHeader>

                <div className="rounded-lg border border-border bg-secondary/30 p-3 text-sm" data-testid="pos-close-summary">
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Datos del turno</div>
                    <div className="flex items-center justify-between py-1"><span className="text-muted-foreground">Apertura</span>
                        <span className="flex flex-wrap justify-end gap-1.5 font-mono tabular-nums" data-testid="pos-close-opening">
                            <span><Chip cur="BS"/> {formatBs(session?.opening_bs)}</span>
                            <span><Chip cur="USD"/> $ {formatUsd(session?.opening_usd)}</span>
                            <span><Chip cur="COP"/> $ {formatCop(session?.opening_cop)}</span>
                        </span>
                    </div>
                    <Row label="Apertura total (Bs)" value={`${formatBs(c.aperturaTotalBs)} Bs`} testid="pos-close-opening-total-bs"/>
                    <Row label="Ventas totales" value={`+${formatBs(c.ventasBs)} Bs`} testid="pos-close-sales-bs"/>
                    <Row label="Premios pagados (neto)" value={`${c.premiosNetosBs > 0 ? "-" : ""}${formatBs(c.premiosNetosBs)} Bs`} testid="pos-close-payouts-bs"/>
                    <Row label="Teórico esperado" value={`${formatBs(c.teoricoBs)} Bs`} strong testid="pos-close-expected-bs"/>
                    <p className="mt-1 text-[11px] text-muted-foreground" data-testid="pos-close-rates">
                        Tasa BCV {formatBs(rates.bs_per_usd)} Bs/USD · Paridad {formatBs(rates.bs_per_cop)} Bs/COP. Premios netos tras compensación.
                    </p>
                    {!rates.bs_per_usd && (
                        <p className="mt-1 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300"><AlertTriangle size={13}/> Tasa no disponible: el cuadre en Bs puede ser inexacto.</p>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1.5"><Label className="flex items-center gap-1.5"><Chip cur="BS"/> Efectivo final</Label>
                        <Input type="number" inputMode="decimal" className="tabular-nums" {...field("closing_bs")} data-testid="pos-close-session-bs"/></div>
                    <div className="space-y-1.5"><Label className="flex items-center gap-1.5"><Chip cur="USD"/> Efectivo final</Label>
                        <Input type="number" inputMode="decimal" className="tabular-nums" {...field("closing_usd")} data-testid="pos-close-session-usd"/></div>
                    <div className="space-y-1.5"><Label className="flex items-center gap-1.5"><Chip cur="COP"/> Efectivo final</Label>
                        <Input type="number" inputMode="numeric" className="tabular-nums" {...field("closing_cop")} data-testid="pos-close-session-cop"/></div>
                    <div className="space-y-1.5 sm:col-span-3"><Label>Notas</Label><Input {...field("notes")} data-testid="pos-close-session-notes"/></div>
                </div>

                <div className="rounded-lg border border-border p-3 text-sm" data-testid="pos-close-diff-card">
                    <Row label="Contado total (Bs)" value={`${formatBs(c.contadoBs)} Bs`} testid="pos-close-counted-bs"/>
                    <div className="flex items-center justify-between gap-3 pt-1.5">
                        <span className="font-bold">Diferencia</span>
                        <span className={`rounded-full border px-3 py-1 text-xs font-bold tabular-nums transition-colors ${st.cls}`} data-testid="pos-close-diff-badge" data-status={status}>
                            {status === "pendiente" ? st.label : `${st.label} (${status === "cuadrado" ? "0,00 Bs" : signedBs(c.diferenciaBs)})`}
                        </span>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pos-close-session-cancel">Cancelar</Button>
                    <Button onClick={onSubmit} disabled={pending} data-testid="pos-close-session-submit">Cerrar caja</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
